import { z } from 'zod'

import type { ProfitabilityRow } from '@/client'

import { pruned } from '@/lib/filters'

import { skuDocumentsSearchFields } from './skuDocumentsSearch'

/** Where the drill is: VentaHub's marketplace → store → SKU. */
export type PartnerLevel = 'marketplace' | 'store' | 'sku'

export const PARTNER_PAGE_SIZES = [25, 50, 100] as const
export const DEFAULT_PARTNER_PAGE_SIZE = 50

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const ids = z.array(z.number().int().positive()).max(50).optional().catch(undefined)
const id = z.number().int().positive().optional().catch(undefined)

/**
 * Partner View's URL: the reports' scope (warehouses, the marketplace, its
 * stores, brands) and the range; whether the report has been generated; the
 * rows opened in place; the store whose every SKU is listed (and its
 * marketplace, for the trail); the search,
 * the sort and the page. Every field optional; defaults apply at read time.
 */
export const partnerViewSearchSchema = z.object({
  warehouseIds: ids.describe('Warehouses to read, by id; empty means every warehouse.'),
  marketplaceId: id.describe('The marketplace to read, by id; absent means every marketplace.'),
  storeIds: ids.describe('Stores to include, by id; empty means every store.'),
  brandIds: ids.describe('Brands to include, by id; empty means every brand.'),
  dateFrom: day
    .optional()
    .catch(undefined)
    .describe('First day of the range, YYYY-MM-DD; the last 30 days when absent.'),
  dateTo: day
    .optional()
    .catch(undefined)
    .describe('Last day of the range, YYYY-MM-DD, inclusive; today when absent.'),
  // Set by Generate report: nothing loads until it is.
  go: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe(
      '1 once Generate report is pressed; nothing loads without it. Any filter or date change clears it.',
    ),
  // The drill: the marketplace opened for its stores, then the store for its SKUs.
  openMarketplace: id.describe('The marketplace of the store drilled into, named in the trail.'),
  openStore: id.describe('The store drilled into: the page lists all of its SKUs.'),
  // The marketplaces (m<id>) and stores (s<id>) opened in place.
  expand: z
    .array(z.string().regex(/^[ms]\d+$/))
    .max(50)
    .optional()
    .catch(undefined)
    .describe('Tree rows opened in place: m<id> for a marketplace, s<id> for a store.'),
  q: z
    .string()
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Search text over the rows (SKU code, name, brand or store).'),
  sort: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(64)
    .optional()
    .catch(undefined)
    .describe('The column id the rows are sorted by; absent is the report’s own order.'),
  order: z
    .enum(['asc', 'desc'])
    .optional()
    .catch(undefined)
    .describe('Sort direction; desc when a sort is set.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the table, from 1; default 1.'),
  limit: z
    .union([z.literal(25), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Rows per page at the SKU level; default 50.'),
  // The SKU whose orders are open in the drawer, its tab and page.
  ...skuDocumentsSearchFields,
})

export type PartnerViewSearch = z.infer<typeof partnerViewSearchSchema>

export const EMPTY_PARTNER_VIEW_SEARCH: PartnerViewSearch = partnerViewSearchSchema.parse({})

/** Reset: every field cleared, so the page is as it first opened. */
export const RESET_PARTNER_VIEW: Partial<PartnerViewSearch> = Object.fromEntries(
  Object.keys(partnerViewSearchSchema.shape).map((key) => [key, undefined]),
)

/** Moving the drill: what was sorted, searched and paged belonged to the level left. */
export const NEW_LEVEL: Partial<PartnerViewSearch> = {
  q: undefined,
  sort: undefined,
  order: undefined,
  page: undefined,
}

/** The level the drill is at. */
export function partnerLevel(search: PartnerViewSearch): PartnerLevel {
  return search.openStore !== undefined ? 'sku' : 'marketplace'
}

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutPartnerViewDefaults(search: PartnerViewSearch): PartnerViewSearch {
  return pruned({
    ...search,
    limit: search.limit === DEFAULT_PARTNER_PAGE_SIZE ? undefined : search.limit,
  })
}

/** Whether a row opens a level below it: a SKU does not, nor a place with no id. */
export function opens(row: ProfitabilityRow, level: PartnerLevel): boolean {
  if (level === 'marketplace')
    return row.marketplace_id !== null && row.marketplace_id !== undefined
  if (level === 'store') return row.store_id !== null && row.store_id !== undefined
  return false
}

/** The row's key at its level: what it is, and its currency. */
export function partnerRowKey(row: ProfitabilityRow, level: PartnerLevel): string {
  const what =
    level === 'marketplace'
      ? row.marketplace_id
      : level === 'store'
        ? row.store_id
        : `${row.store_id ?? 'none'}|${row.sku_id ?? 'none'}`
  return `${what ?? 'none'}|${row.currency_code}`
}
